import React, { useState, useRef } from 'react';
import { Box } from '@mui/material';
import { useEmailSignatures } from 'team/hooks/useEmailSignatures';
import { useEmailSignatureCard } from 'team/hooks/useEmailSignatureCard';
import { useEmailSignatureContext } from 'contexts/EmailSignatureContext';
import { assignMembersToEmailSignature } from 'team/apis/teamEmailSignatureApi';
import TeamEmailSignatureNavbar from 'team/components/emailSignature/TeamEmailSignatureNavbar';
import TeamEmailSignatureEmptyState from 'team/components/emailSignature/TeamEmailSignatureEmptyState';
import TeamEmailSignatureList from 'team/components/emailSignature/TeamEmailSignatureList';
import TeamCreateEmailSignatureHeader from 'team/components/emailSignature/TeamCreateEmailSignatureHeader';
import TeamCreateEmailSignatureForm from 'team/components/emailSignature/TeamCreateEmailSignatureForm';
import TeamCreateEmailSignaturePreview from 'team/components/emailSignature/TeamCreateEmailSignaturePreview';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamEmailSignatureContainer = ({ user, team }) => {
    const [view, setView] = useState('list'); // 'list', 'create', or 'edit'
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [signatureName, setSignatureName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const signatureRef = useRef(null);
    
    const teamId = team?._id || team?.id;
    const { 
        emailSignatureTemplates, 
        templatesCount, 
        loading: templatesLoading,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        refreshTemplates
    } = useEmailSignatures(teamId);
    
    // Pass team to hook so it can find team owner/admin member's card
    const { defaultCardId, loading, emailSignatureState, setLoading } = useEmailSignatureCard(team);
    const { 
        setEmailSignatureName,
        setEmailSignatureCompany,
        setEmailSignatureJobTitle,
        setEmailSignaturePhoneNumber,
        setEmailSignatureLocation,
        setEmailSignatureDisclaimer,
        setEmailSignatureIncludeBannerImage,
        setEmailSignatureIncludeQRCode,
        setEmailSignatureBannerImageUrl,
        setEmailSignatureShowName,
        setEmailSignatureShowPronouns,
        setEmailSignatureShowJobTitle,
        setEmailSignatureShowCompany,
        setEmailSignatureShowLocation,
        setEmailSignatureShowPhoneNumber,
        setEmailSignatureShowProfilePic,
        setEmailSignatureShowCompanyLogo,
        addEmailSignatureLinkType,
        removeEmailSignatureLinkType
    } = useEmailSignatureContext();

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSetUpEmailSignature = () => {
        // TODO: Navigate to email signature setup/integrations page
    };

    const handleCreateEmailSignature = () => {
        setEditingTemplateId(null);
        setSignatureName('');
        setView('create');
    };

    const handleBackToEmailSignatures = () => {
        setView('list');
        setEditingTemplateId(null);
        setSignatureName('');
    };

    const handleCancel = () => {
        setSignatureName('');
        setEditingTemplateId(null);
        setView('list');
    };

    const handleUpdate = async () => {
        if (!defaultCardId || !teamId) {
            showSnackbar('Missing required information', 'error');
            return;
        }

        if (!signatureName.trim()) {
            showSnackbar('Signature name is required', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const templateData = {
                signatureName: signatureName.trim(),
                ...emailSignatureState[defaultCardId]
            };

            if (editingTemplateId) {
                // Update existing template
                await updateTemplate(editingTemplateId, templateData);
                showSnackbar('Email signature template updated successfully', 'success');
            } else {
                // Create new template
                await createTemplate(templateData);
                showSnackbar('Email signature template created successfully', 'success');
            }

            setView('list');
            setEditingTemplateId(null);
            setSignatureName('');
        } catch (error) {
            console.error('Error saving email signature template:', error);
            showSnackbar(error.message || 'Failed to save email signature template', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (template) => {
        if (!template || !template._id || !defaultCardId) {
            showSnackbar('Unable to load template for editing', 'error');
            return;
        }

        setEditingTemplateId(template._id);
        setSignatureName(template.signatureName || '');

        // Populate email signature state with template data
        if (template.emailSignatureShowName !== undefined) {
            setEmailSignatureShowName(defaultCardId, template.emailSignatureShowName);
        }
        if (template.emailSignatureShowPronouns !== undefined) {
            setEmailSignatureShowPronouns(defaultCardId, template.emailSignatureShowPronouns);
        }
        if (template.emailSignatureShowJobTitle !== undefined) {
            setEmailSignatureShowJobTitle(defaultCardId, template.emailSignatureShowJobTitle);
        }
        if (template.emailSignatureShowCompany !== undefined) {
            setEmailSignatureShowCompany(defaultCardId, template.emailSignatureShowCompany);
        }
        if (template.emailSignatureShowLocation !== undefined) {
            setEmailSignatureShowLocation(defaultCardId, template.emailSignatureShowLocation);
        }
        if (template.emailSignatureShowPhoneNumber !== undefined) {
            setEmailSignatureShowPhoneNumber(defaultCardId, template.emailSignatureShowPhoneNumber);
        }
        if (template.emailSignatureShowProfilePic !== undefined) {
            setEmailSignatureShowProfilePic(defaultCardId, template.emailSignatureShowProfilePic);
        }
        if (template.emailSignatureShowCompanyLogo !== undefined) {
            setEmailSignatureShowCompanyLogo(defaultCardId, template.emailSignatureShowCompanyLogo);
        }
        if (template.emailSignatureIncludeQRCode !== undefined) {
            setEmailSignatureIncludeQRCode(defaultCardId, template.emailSignatureIncludeQRCode);
        }
        if (template.emailSignatureIncludeBannerImage !== undefined) {
            setEmailSignatureIncludeBannerImage(defaultCardId, template.emailSignatureIncludeBannerImage);
        }
        if (template.emailSignatureDisclaimer !== undefined) {
            setEmailSignatureDisclaimer(defaultCardId, template.emailSignatureDisclaimer);
        }
        if (template.emailSignatureBannerImageUrl !== undefined) {
            setEmailSignatureBannerImageUrl(defaultCardId, template.emailSignatureBannerImageUrl);
        }
        if (template.emailSignatureLinkTypes && Array.isArray(template.emailSignatureLinkTypes)) {
            // Clear existing links and add template links
            const currentLinks = emailSignatureState[defaultCardId]?.emailSignatureLinkTypes || [];
            currentLinks.forEach(link => removeEmailSignatureLinkType(defaultCardId, link));
            template.emailSignatureLinkTypes.forEach(link => addEmailSignatureLinkType(defaultCardId, link));
        }

        setView('create'); // Use same form view for editing
    };

    const handleDelete = async (templateId) => {
        if (!templateId) {
            showSnackbar('Template ID is required', 'error');
            return;
        }

        try {
            await deleteTemplate(templateId);
            showSnackbar('Email signature template deleted successfully', 'success');
        } catch (error) {
            console.error('Error deleting email signature template:', error);
            showSnackbar(error.message || 'Failed to delete email signature template', 'error');
        }
    };

    const handleAssignMembers = async (data) => {
        try {
            const { templateId, assignMemberIds, unassignMemberIds, setAsDefault } = data;
            
            if (!templateId || !team?._id) {
                showSnackbar('Template ID and Team ID are required', 'error');
                return;
            }

            await assignMembersToEmailSignature(team._id, templateId, {
                assignMemberIds,
                unassignMemberIds,
                setAsDefault
            });
            
            showSnackbar('Members assigned successfully', 'success');
            // Refresh templates list
            await refreshTemplates();
        } catch (error) {
            console.error('Error assigning members:', error);
            showSnackbar(error.message || 'Failed to assign members', 'error');
        }
    };

    // Show create/edit form view
    if (view === 'create') {
        return (
            <Box sx={{ height: '100vh', padding: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <TeamCreateEmailSignatureHeader 
                    onBack={handleBackToEmailSignatures} 
                    isEditing={!!editingTemplateId}
                />

                {/* White Container with Two Column Layout */}
                <Box sx={{
                    flex: 1,
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'row',
                }}>
                    <TeamCreateEmailSignatureForm
                        signatureName={signatureName}
                        onSignatureNameChange={setSignatureName}
                        defaultCardId={defaultCardId}
                        emailSignatureState={emailSignatureState}
                        onCancel={handleCancel}
                        onUpdate={handleUpdate}
                        isCreating={!editingTemplateId}
                        isSubmitting={isSubmitting}
                    />

                    <TeamCreateEmailSignaturePreview
                        loading={loading}
                        defaultCardId={defaultCardId}
                        emailSignatureState={emailSignatureState}
                        signatureRef={signatureRef}
                        setLoading={setLoading}
                    />
                </Box>

                <TeamNotificationSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    handleClose={handleCloseSnackbar}
                />
            </Box>
        );
    }

    // Show list view (default)
    return (
        <Box sx={{ width: '100%', padding: 2 }}>
            <TeamEmailSignatureNavbar
                templatesCount={templatesCount}
                onSetUpEmailSignature={handleSetUpEmailSignature}
                onCreateEmailSignature={handleCreateEmailSignature}
            />

            {templatesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Box>Loading email signatures...</Box>
                </Box>
            ) : templatesCount === 0 ? (
                <TeamEmailSignatureEmptyState />
            ) : (
                <TeamEmailSignatureList
                    templates={emailSignatureTemplates}
                    isLoading={templatesLoading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAssignMembers={handleAssignMembers}
                    teamId={teamId}
                    defaultCardId={defaultCardId}
                    emailSignatureState={emailSignatureState}
                />
            )}

            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleCloseSnackbar}
            />
        </Box>
    );
};

export default TeamEmailSignatureContainer;
